import FiscalFundingSummary from "../../../finance/FiscalFundingSummary.jsx";
import { isInterventionCancelledBeforeStart } from "../../../../lib/interventionCancellation";
import "./InterventionModal.css";
import FutureInterventionDateModal from "./FutureInterventionDateModal.jsx";
import { FUTURE_INTERVENTION_START_ERROR, getFutureInterventionDeliveryError } from "../../../../lib/interventionDeliveryDate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autosuggest,
  Badge,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  FormField,
  Grid,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
  Table,
  Textarea,
} from "@cloudscape-design/components";
import { apiFetch } from "../../../../auth/apiClient.js";
import useCurrentUser from "../../../../hooks/useCurrentUser.js";
import {
  formatCurrencyDisplay,
  getCurrencyInputDisplayValue,
  hasCurrencyPrecision,
  normalizeCurrencyAmount,
  parseCurrencyAmount,
} from "../../../../utils/currencyFormat.js";
import {
  formatInterventionStatusLabel,
  isInterventionClosedStatus,
  normalizeInterventionStatus,
  resolveInterventionStateFields,
} from "../../../../utils/interventionStatus.js";
import {
  isEducationInterventionCode as isEducationCode,
  isEmployerInterventionCode as isEmployerCode,
  isWageSubsidyInterventionCode as isWageSubsidyCode,
  requiresExternalPartnerForInterventionCode as requiresExternalPartnerForCode,
  requiresNocForInterventionCode as requiresNocForCode,
} from "../../../../utils/interventionCodeRules.js";
import { resolveInterventionPostingContextForForm } from "../../../../utils/interventionPostingContext.js";

import { buildInterventionPaymentSummary, paymentTypeLabel } from "../../../../lib/interventionPaymentSummary.js";
import { getPaymentTerminology } from "../../../../lib/paymentTerminology.js";

const { matchesFundingChoice } = require("../../../../lib/budgetFiscal");

const BASE_STATUS_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "in_progress", label: "In progress" },
  { value: "suspended", label: "Suspended" },
];

const CLOSE_STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const CURRENCY_AMOUNT_RANGE_MESSAGE = "must be between 0 and 999999 with no more than two decimal places.";
const MAX_INTERVENTION_DURATION_DAYS = 999;
const DIRECT_EDIT_FIELDS = ["notes", "itpDetails", "wageSubsidyDetails", "noc", "nocVersion"];
const normalizeDetailValue = value => String(value ?? "").trim() || null;
const POSTING_CONTEXT_OPTIONS = [
  { value: "external", label: "External (region/PTMA)" },
  { value: "internal", label: "Internal (NWAC)" },
];

const calculateDurationDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs < 0) return null;
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

const normalizeApprovedCostLine = (raw, index = 0) => {
  if (!raw || typeof raw !== "object") return null;
  const recurrenceRaw =
    raw.recurrence && typeof raw.recurrence === "object" ? raw.recurrence : {};
  const payeeRaw = raw.payee && typeof raw.payee === "object" ? raw.payee : {};
  const amount =
    raw.amount === null || typeof raw.amount === "undefined" || raw.amount === ""
      ? null
      : Number(raw.amount);
  const amountPerPeriod =
    recurrenceRaw.amountPerPeriod === null ||
    typeof recurrenceRaw.amountPerPeriod === "undefined" ||
    recurrenceRaw.amountPerPeriod === ""
      ? null
      : Number(recurrenceRaw.amountPerPeriod);
  const occurrences =
    recurrenceRaw.occurrences === null || typeof recurrenceRaw.occurrences === "undefined"
      ? null
      : Number(recurrenceRaw.occurrences);
  if (!(Number.isFinite(amount) && amount > 0) && !(Number.isFinite(amountPerPeriod) && amountPerPeriod > 0)) {
    return null;
  }
  const hasRecurringAmount = recurrenceRaw.enabled !== false &&
    Number.isFinite(amountPerPeriod) && amountPerPeriod > 0 &&
    Number.isFinite(occurrences) && occurrences > 0;
  const recurringAmountMatchesTotal = !(Number.isFinite(amount) && amount > 0) ||
    Math.round(amountPerPeriod * 100) * occurrences === Math.round(amount * 100);
  return {
    id: raw.id || `approved-line-${index + 1}`,
    approvedAmount: Number.isFinite(amount) ? amount : Number.isFinite(amountPerPeriod) && occurrences > 0 ? amountPerPeriod * occurrences : null,
    paymentType: raw.type || raw.paymentType || raw.payment_type || "—",
    payeeName: String(payeeRaw.name || raw.payeeName || raw.payee_name || "").trim() || "—",
    amount:
      hasRecurringAmount && recurringAmountMatchesTotal
        ? `${formatCurrencyDisplay(String(amountPerPeriod))} × ${occurrences}`
        : Number.isFinite(amount) && amount > 0
          ? formatCurrencyDisplay(String(amount))
          : Number.isFinite(amountPerPeriod) && amountPerPeriod > 0 && Number.isFinite(occurrences) && occurrences > 0
            ? formatCurrencyDisplay(String(amountPerPeriod * occurrences))
            : Number.isFinite(amountPerPeriod) && amountPerPeriod > 0
              ? `${formatCurrencyDisplay(String(amountPerPeriod))} / period`
              : "Not set",
  };
};

const resolveApprovedCostLines = intervention => {
  if (!intervention || typeof intervention !== "object") return [];
  const metadata =
    intervention.metadata && typeof intervention.metadata === "object" ? intervention.metadata : {};
  const snapshot =
    metadata.snapshot && typeof metadata.snapshot === "object" ? metadata.snapshot : {};
  const source =
    (Array.isArray(intervention.costLines) && intervention.costLines) ||
    (Array.isArray(metadata.costLines) && metadata.costLines) ||
    (Array.isArray(snapshot.costLines) && snapshot.costLines) ||
    [];
  return source.map((line, index) => normalizeApprovedCostLine(line, index)).filter(Boolean);
};

const defaultForm = {
  code: "",
  status: "approved",
  startDate: "",
  endDate: "",
  durationDays: "",
  outcome: "",
  cost: "",
  fundingStream: "",
  notes: "",
  noc: "",
  nocVersion: "",
  postingContext: "external",
  deliveryMode: "partner",
  institution: "",
  programName: "",
  itpDetails: "",
  wageSubsidyDetails: "",
};

const FORM_KEYS = Object.keys(defaultForm);

const CANONICAL_INTERVENTION_LABELS = {
  "1": "Career research and exploration",
  "2": "Diagnostic assessment",
  "3": "Employment counselling",
  "4": "Skills development - Essential skills",
  "5": "Skills development - Academic upgrading",
  "6": "Work experience - Job creation partnerships",
  "7": "Work experience - Wage subsidy",
  "8": "Work experience - Student employment",
  "9": "Occupational skills training - Certificate",
  "10": "Occupational skills training - Diploma",
  "11": "Occupational skills training - Degree",
  "12": "Occupational skills training - Apprenticeship",
  "13": "Occupational skills training - Vocational",
  "14": "Self-employment",
  "15": "Job search preparation strategies",
  "16": "Job starts supports",
  "17": "Employer referral",
  "18": "Employment retention supports",
  "19": "Referral to agencies",
  "20": "Pre-career development",
};

const pickDefaultNocVersion = options => {
  if (!Array.isArray(options) || options.length === 0) return "";
  const preferred = options.find(option => option?.value === "2021");
  return preferred?.value || options[0]?.value || "";
};

const normaliseFormNumbers = value =>
  typeof value === "number" && Number.isFinite(value) ? String(value) : "";

const clampInterventionDurationDaysForIlmp = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return "";
  return String(Math.min(Math.round(numeric), MAX_INTERVENTION_DURATION_DAYS));
};

const normaliseDurationFormNumber = value => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return clampInterventionDurationDaysForIlmp(value);
  }
  if (typeof value === "string" && value.trim()) {
    return clampInterventionDurationDaysForIlmp(value);
  }
  return "";
};

const normaliseInterventionText = value => (typeof value === "string" ? value : "");

const parseOptionalCurrencyAmount = value => {
  if (value === null || typeof value === "undefined" || value === "") return null;
  const numeric = parseCurrencyAmount(value);
  if (!Number.isFinite(numeric)) return NaN;
  return normalizeCurrencyAmount(numeric);
};

const isValidOptionalCurrencyAmount = value => {
  const numeric = parseOptionalCurrencyAmount(value);
  return (
    numeric === null ||
    (Number.isFinite(numeric) && numeric >= 0 && numeric <= 999999 && hasCurrencyPrecision(value))
  );
};

const resolveModalInterventionState = (record, fallbackStatus = "approved") =>
  resolveInterventionStateFields(record, { fallbackStatus });

const normalizeEditableInterventionStatus = (record, fallbackStatus = "approved") =>
  resolveModalInterventionState(record, fallbackStatus).effectiveStatus ||
  normalizeInterventionStatus(
    record && typeof record === "object" ? record?.status ?? record?.interventionStatus ?? null : record,
    fallbackStatus
  );

const buildInitialForm = (mode, intervention) => {
  if (mode === "edit" && intervention) {
    const metadata = intervention.metadata && typeof intervention.metadata === "object" ? intervention.metadata : {};
    return {
      code: intervention.code ? String(intervention.code) : "",
      status: normalizeEditableInterventionStatus(intervention, "approved"),
      startDate: intervention.startDate || "",
      endDate: intervention.endDate || "",
      durationDays: intervention.endDate ? normaliseDurationFormNumber(intervention.durationDays) : "",
      outcome: intervention.outcome || "",
      cost: normaliseFormNumbers(intervention.cost),
      fundingStream: intervention.fundingStream || "",
      notes: intervention.notes || "",
      noc: intervention.noc || "",
      nocVersion: intervention.nocVersion || "",
      postingContext: intervention.postingContext || intervention.metadata?.postingContext || "external",
      deliveryMode:
        intervention.deliveryMode === "in_house" || metadata.deliveryMode === "in_house" ? "in_house" : "partner",
      institution: normaliseInterventionText(
        intervention.institution ??
          metadata.institution ??
          metadata.trainingInstitution ??
          metadata.training_institution ??
          ""
      ),
      programName: normaliseInterventionText(
        intervention.programName ?? metadata.programName ?? metadata.program_name ?? ""
      ),
      itpDetails: normaliseInterventionText(
        Object.hasOwn(intervention, "itpDetails") ? intervention.itpDetails : metadata.itpDetails ?? metadata.itp_details ?? ""
      ),
      wageSubsidyDetails: normaliseInterventionText(
        Object.hasOwn(intervention, "wageSubsidyDetails") ? intervention.wageSubsidyDetails :
          metadata.wageSubsidyDetails ??
          metadata.wage_subsidy_details ??
          ""
      ),
    };
  }
  return { ...defaultForm };
};

const isClosedStatusValue = status => isInterventionClosedStatus(status);

const normaliseOutcomeForStatus = (status, currentOutcome) => {
  const interventionState = resolveModalInterventionState(status);
  const normalized = interventionState.effectiveStatus || normalizeInterventionStatus(status);
  if (!isInterventionClosedStatus(normalized)) {
    return "";
  }
  if (currentOutcome) {
    return String(currentOutcome).trim();
  }
  return "";
};

const buildCloseForm = intervention => {
  const interventionState = resolveModalInterventionState(intervention, "completed");
  const resolvedStatus = interventionState.deliveryStatus === "cancelled" ? "cancelled" : "completed";
  const resolvedOutcome = intervention?.outcome ? String(intervention.outcome).trim() : "";
  const actualCandidates = [
    intervention?.actualAmount,
    intervention?.metadata?.actualAmount,
  ];
  let actualAmount = "";
  for (const candidate of actualCandidates) {
    if (candidate === null || typeof candidate === "undefined") continue;
    if (typeof candidate === "string" && !candidate.trim()) continue;
    const numeric = parseCurrencyAmount(candidate);
    if (Number.isFinite(numeric)) {
      actualAmount = String(numeric);
      break;
    }
  }
  return {
    status: resolvedStatus,
    outcome: resolvedOutcome,
    completionDate: intervention?.endDate || "",
    actualAmount,
  };
};

const formatDetailDate = value => {
  if (!value) return "Not recorded";
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-CA", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
  }).format(date);
};

const emptyNocSearch = () => Promise.resolve([]);

const SummaryField = ({ label, children, wide = false }) => (
  <div className={wide ? "intervention-summary-field intervention-summary-wide" : "intervention-summary-field"}>
    <dt>{label}</dt>
    <dd>{children || "Not recorded"}</dd>
  </div>
);

const InterventionModal = ({
  caseId = null,
  visible,
  mode = "create",
  intervention = null,
  plan = null,
  onDismiss,
  onSubmit,
  onSubmitDetails,
  canEditDetails = false,
  onClose,
  canClose = false,
  startInCloseMode = false,
  readOnly = false,
  planStartDate = "",
  codeOptions = [],
  codesLoading = false,
  outcomeOptions = [],
  outcomesLoading = false,
  fundingStreamOptions = [],
  fundingStreamsLoading = false,
  nocVersions = [],
  nocVersionsLoading = false,
  onSearchNocCodes = emptyNocSearch,
  onPreparePayment,
  onOpenPayment,
  paymentSubmissionMode = "email",
  pendingRevision = null,
  pendingRevisionStatusLabel = null,
  onRevise,
  revisionActionLabel = "Revise approved intervention",
}) => {
  const currentUser = useCurrentUser();
  const role = currentUser?.role ? currentUser.role : null;
  const canonicalRole = role === "Regional Manager" ? "Regional Manager" : role;
  const isAssessor = canonicalRole === "ISET Coordinator";
  const canSelectPostingContext = canonicalRole === "Regional Manager" || canonicalRole === "NWAC Administrator";
  const [form, setForm] = useState({ ...defaultForm });
  const initialFormRef = useRef({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [futureDateError, setFutureDateError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [nocDisplay, setNocDisplay] = useState(null);
  const [nocLookupText, setNocLookupText] = useState(null);
  const [nocSuggestions, setNocSuggestions] = useState([]);
  const [nocSuggestionsLoading, setNocSuggestionsLoading] = useState(false);
  const nocSearchSequence = useRef(0);
  const nocCodesByVersion = useRef(new Map());
  const [isClosing, setIsClosing] = useState(false);
  const [isEditing, setIsEditing] = useState(mode !== "edit");
  const [showCloseGuidance, setShowCloseGuidance] = useState(true);
  const [closeForm, setCloseForm] = useState(buildCloseForm(intervention));
  const [actualCostEdited, setActualCostEdited] = useState(false);
  const [potOptions, setPotOptions] = useState([]);
  const inheritedBudgetPot = plan?.budgetPotId || plan?.budget_pot_id || plan?.budgetPot || plan?.budget_pot || "";
  const inheritedBudgetPotDisplay =
    plan?.budgetPotDisplayLabel ||
    plan?.budgetPotLabel ||
    plan?.budget_pot_label ||
    [plan?.budgetPotCode || plan?.budget_pot_code, plan?.budgetPotName || plan?.budget_pot_name]
      .filter(Boolean)
      .join(" - ");
  const inheritedFundingStream = plan?.fundingStream || plan?.funding_stream || "";
  const recordedFundingStream = intervention?.fundingStreamDecision ?? intervention?.funding_stream_decision ?? "";
  const fundingStreamReference = mode === "edit" && recordedFundingStream !== inheritedFundingStream
    ? `Action plan: ${inheritedFundingStream || "Not set"}.`
    : null;

  useEffect(() => {
    nocSearchSequence.current += 1;
    nocCodesByVersion.current.clear();
    setNocLookupText(null);
    setActualCostEdited(false);
    if (!visible) {
      const blankForm = { ...defaultForm };
      setForm(blankForm);
      initialFormRef.current = { ...blankForm };
      setLoading(false);
      setError(null);
      setValidationError(null);
      setFieldErrors({});
      setNocSuggestions([]);
      setNocSuggestionsLoading(false);
      setIsClosing(false);
      setIsEditing(mode !== "edit");
      setCloseForm(buildCloseForm(null));
      return;
    }

    const baseForm = buildInitialForm(mode, intervention);
    const prepared = (() => {
      const draft = { ...defaultForm, ...baseForm };
      if (!requiresNocForCode(draft.code)) {
        draft.noc = "";
        draft.nocVersion = "";
      }
      // Inherit funding stream from plan (read-only) if provided.
      if (plan) {
        draft.fundingStream = inheritedFundingStream || "";
      }
      draft.postingContext = resolveInterventionPostingContextForForm({
        mode,
        intervention,
        plan,
        fallback: draft.postingContext,
      });
      return draft;
    })();

    prepared.status = normalizeEditableInterventionStatus(prepared.status, "approved");
    prepared.outcome = normaliseOutcomeForStatus(prepared.status, prepared.outcome);

    initialFormRef.current = { ...prepared };
    setForm(prepared);
    setLoading(false);
    setError(null);
    setFutureDateError(null);
    setNocSuggestions([]);
    setNocSuggestionsLoading(false);
    const isClosed = intervention && isClosedStatusValue(intervention.status);
    setIsClosing(Boolean((startInCloseMode && canClose && mode === "edit") || (mode === "edit" && isClosed && !canEditDetails)));
    setIsEditing(mode !== "edit");
    setCloseForm(buildCloseForm(intervention));
  }, [visible, mode, intervention, startInCloseMode, canClose, canEditDetails, plan, inheritedFundingStream]);

  useEffect(() => {
    if (!isAssessor) return;
    if (form.postingContext !== "external") {
      setForm(current => ({ ...current, postingContext: "external" }));
    }
  }, [isAssessor, form.postingContext]);

  useEffect(() => {
    let cancelled = false;
    const loadPots = async () => {
      if (!visible) {
        setPotOptions([]);
        return;
      }
      try {
        let resp = await apiFetch(`/api/reference/budget-pots-lite?chargeableOnly=0&selectedPotId=${encodeURIComponent(inheritedBudgetPot || "")}`);
        if (!resp || !resp.ok) {
          resp = await apiFetch(`/api/reference/budget-pots-lite?chargeableOnly=0&selectedPotId=${encodeURIComponent(inheritedBudgetPot || "")}`);
        }
        const data = resp && resp.ok ? await resp.json() : [];
        if (cancelled) return;
        const isFundingStream = pot => {
          const potType =
            pot?.pot_type ??
            pot?.potType ??
            pot?.type ??
            pot?.nodeType ??
            pot?.metadata?.pot_type ??
            pot?.metadata?.nodeType ??
            "";
          const norm = String(potType).trim().toLowerCase().replace(/[_\s]+/g, " ");
          return norm === "funding stream";
        };
        const opts = (Array.isArray(data) ? data : [])
          .filter(isFundingStream)
          .map(item => {
            const value = item?.id ?? item?.value ?? item?.code ?? null;
            if (!value) return null;
            const code = item?.code || "";
            const name = item?.name || item?.description || "";
            const inactiveBadge = item?.isActive === false ? " (inactive)" : "";
            const label = [code, name].filter(Boolean).join(" - ") + inactiveBadge || String(value);
            return {
              value: String(value),
              fiscalPotIds: item.fiscalPotIds || [],
              label: label || String(value),
              description: name || undefined,
            };
          })
          .filter(Boolean);
        setPotOptions(opts);
      } catch (_) {
        if (!cancelled) setPotOptions([]);
      }
    };
    loadPots();
    return () => {
      cancelled = true;
    };
  }, [visible, inheritedBudgetPot]);

  useEffect(() => {
    if (isClosing) {
      setShowCloseGuidance(true);
    }
  }, [isClosing]);

  const interventionState = resolveModalInterventionState(intervention, "approved");
  const isClosedIntervention = isInterventionClosedStatus(interventionState);
  // Approval freezes the contract facts. Operational detail editing has its own
  // explicit permission and save route; generic edit access cannot unlock them.
  const isDetailsOnly = mode === "edit" && interventionState.reviewStatus === "approved";
  const canEditApprovedDetails = isDetailsOnly && canEditDetails &&
    Boolean(intervention?.detailsVersion) && typeof onSubmitDetails === "function";
  const isAccessReadOnly = isDetailsOnly
    ? !canEditApprovedDetails
    : Boolean(readOnly || (mode === "edit" && isClosedIntervention && !canClose));
  const isViewMode = mode === "edit" && !isEditing;
  const isFormReadOnly = isAccessReadOnly || isViewMode || (mode === "edit" && isClosing);
  // canClose is the caller's distinct delivery permission. Frozen assessment
  // fields must not disable the separately authorized outcome/date closeout.
  const isCloseReadOnly = !canClose || !isClosing;
  const modalHeader =
    isClosing && canClose
      ? "Close intervention"
      : mode === "edit"
      ? isFormReadOnly
        ? "View intervention"
        : "Edit intervention"
      : "Add intervention";
  const modalHeaderContent = (
    <Header
      variant="h2"
      actions={
        pendingRevision?.id ? <Badge color="blue">Revision pending</Badge> : undefined
      }
    >
      {modalHeader}
    </Header>
  );
  useEffect(() => {
    if (!visible) return;
    if (mode !== "edit") return;
    if (intervention && isClosedStatusValue(intervention.status) && !canEditDetails) {
      setIsClosing(true);
    }
  }, [visible, mode, intervention, canEditDetails]);

  const selectOptions = useMemo(() => {
    const formatted = (Array.isArray(codeOptions) ? codeOptions : [])
      .map(item => {
        if (!item) return null;
        const value = item.code ? String(item.code).trim() : null;
        const label = item.label ? String(item.label).trim() : null;
        if (!value || !label) return null;
        const padded = value.length === 1 ? `0${value}` : value;
        return {
          value,
          label: `${padded} – ${label}`,
        };
      })
      .filter(Boolean);
    if (form.code && !formatted.some(option => option.value === form.code)) {
      const fallbackLabel = CANONICAL_INTERVENTION_LABELS[String(form.code)] || "(legacy value)";
      formatted.push({
        value: form.code,
        label: `${form.code} – ${fallbackLabel}`,
        disabled: true,
      });
    }
    return formatted;
  }, [codeOptions, form.code]);

  const statusOptions = useMemo(() => {
    const options = [...BASE_STATUS_OPTIONS];
    const current = normalizeEditableInterventionStatus(form.status, "approved");
    if (current && !options.some(option => option.value === current)) {
      options.push({ value: current, label: formatInterventionStatusLabel(current), disabled: true });
    }
    return options;
  }, [form.status]);

  const selectedStatusOption = useMemo(
    () => statusOptions.find(option => option.value === normalizeEditableInterventionStatus(form.status, "approved")) || statusOptions[0],
    [statusOptions, form.status]
  );
  const isEducationIntervention = useMemo(() => isEducationCode(form.code), [form.code]);
  const isEmployerIntervention = useMemo(() => isEmployerCode(form.code), [form.code]);
  const isWageSubsidyIntervention = useMemo(() => isWageSubsidyCode(form.code), [form.code]);
  const requiresExternalPartner = useMemo(() => requiresExternalPartnerForCode(form.code), [form.code]);
  const selectedDeliveryModeOption = useMemo(
    () =>
      form.deliveryMode === "in_house"
        ? { value: "in_house", label: "In-house (no external partner)" }
        : { value: "partner", label: "External delivery partner" },
    [form.deliveryMode]
  );

  const selectedCloseStatusOption = useMemo(
    () => CLOSE_STATUS_OPTIONS.find(option => option.value === closeForm.status) || CLOSE_STATUS_OPTIONS[0],
    [closeForm.status]
  );

  const selectedCodeOption = useMemo(
    () => selectOptions.find(option => option.value === form.code) || null,
    [selectOptions, form.code]
  );

  const requiresNoc = useMemo(() => {
    const numeric = Number(form.code);
    return Number.isFinite(numeric) && numeric >= 6 && numeric <= 13;
  }, [form.code]);

  const outcomeSelectOptions = useMemo(() => {
    const formatted = (Array.isArray(outcomeOptions) ? outcomeOptions : [])
      .map(item => {
        if (!item) return null;
        const value = item.code ? String(item.code).trim() : null;
        const label = item.label ? String(item.label).trim() : null;
        if (!value || !label) return null;
        const padded = value.length === 1 ? `0${value}` : value;
        return {
          value,
          label: `${padded} - ${label}`,
        };
      })
      .filter(Boolean);

    if (form.outcome && !formatted.some(option => option.value === form.outcome)) {
      formatted.push({
        value: form.outcome,
        label: `${form.outcome} - (legacy value)`,
        disabled: true,
      });
    }

    return formatted;
  }, [outcomeOptions, form.outcome]);

  const selectedCloseOutcomeOption = useMemo(
    () => outcomeSelectOptions.find(option => option.value === closeForm.outcome) || null,
    [outcomeSelectOptions, closeForm.outcome]
  );

  const outcomeLabel = useMemo(() => {
    if (!form.outcome) return "";
    const match = outcomeSelectOptions.find(option => option.value === form.outcome);
    return match ? match.label : form.outcome;
  }, [form.outcome, outcomeSelectOptions]);

  const nocVersionOptions = useMemo(() => {
    const formatted = (Array.isArray(nocVersions) ? nocVersions : [])
      .map(item => {
        if (!item) return null;
        const value = item.code ? String(item.code).trim() : null;
        const label = item.label ? String(item.label).trim() : null;
        if (!value || !label) return null;
        return {
          value,
          label: `${value} – ${label}`,
          description: item.description || null,
        };
      })
      .filter(Boolean);
    if (form.nocVersion && !formatted.some(option => option.value === form.nocVersion)) {
      formatted.push({
        value: form.nocVersion,
        label: `${form.nocVersion} – (legacy value)`,
        disabled: true,
      });
    }
    return formatted;
  }, [nocVersions, form.nocVersion]);

  const selectedNocVersionOption = useMemo(
    () => nocVersionOptions.find(option => option.value === form.nocVersion) || null,
    [nocVersionOptions, form.nocVersion]
  );

  // Resolve the recorded ILMP code/version; never infer an occupation from the program.
  useEffect(() => {
    if (!visible || !requiresNoc || !form.noc || !form.nocVersion) return;
    let cancelled = false;
    const code = String(form.noc).trim();
    const version = String(form.nocVersion).trim();
    setNocDisplay({ code, version, loading: true, title: null });
    const resolveTitle = async () => {
      let title = null;
      try {
        const results = await onSearchNocCodes({ query: code, version });
        const match = (Array.isArray(results) ? results : []).find(item =>
          String(item.code).trim() === code && String(item.version).trim() === version
        );
        title = match?.title?.trim() || null;
      } catch {
        // A reference lookup failure must not hide the recorded code or prevent editing.
      }
      if (!cancelled) setNocDisplay({ code, version, loading: false, title });
    };
    resolveTitle();
    return () => { cancelled = true; };
  }, [visible, requiresNoc, form.noc, form.nocVersion, onSearchNocCodes]);

  const currentNocDisplay = nocDisplay?.code === String(form.noc).trim() &&
    nocDisplay?.version === String(form.nocVersion).trim() ? nocDisplay : null;
  const nocDisplayTitle = !form.noc ? "Not recorded" : !form.nocVersion ? "Occupation name unavailable" :
    !currentNocDisplay || currentNocDisplay.loading ? "Loading occupation…" :
    currentNocDisplay.title || "Occupation name unavailable";

  const inheritedBudgetPotLabel = useMemo(() => {
    const match = potOptions.find(opt => matchesFundingChoice(opt, inheritedBudgetPot));
    return match?.label || inheritedBudgetPotDisplay || (inheritedBudgetPot ? String(inheritedBudgetPot) : "Not set");
  }, [potOptions, inheritedBudgetPot, inheritedBudgetPotDisplay]);
  const selectedPostingContext = useMemo(
    () => POSTING_CONTEXT_OPTIONS.find(option => option.value === form.postingContext) || POSTING_CONTEXT_OPTIONS[0],
    [form.postingContext]
  );

  const costInputValue = useMemo(() => form.cost, [form.cost]);
  const [isCostFocused, setIsCostFocused] = useState(false);
  const [isActualCostFocused, setIsActualCostFocused] = useState(false);
  const [packetLineItems, setPacketLineItems] = useState([]);
  const [paymentOwnerId, setPaymentOwnerId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentView, setPaymentView] = useState("unsent");
  const paymentTerms = getPaymentTerminology(paymentSubmissionMode);

  useEffect(() => {
    let cancelled = false;
    setPaymentView("unsent");

    const loadPacketLines = async () => {
      setPacketLineItems([]);
      setPaymentOwnerId(null);
      setPaymentError(null);
      if (!visible || !intervention?.id) {
        setPaymentLoading(false);
        return;
      }
      setPaymentLoading(true);
      try {
        const response = await apiFetch(`/api/interventions/${intervention.id}/payment-lines`, {
          method: "GET",
        });
        if (!response.ok) {
          throw new Error("Unable to load the payment schedule.");
        }
        const data = await response.json();
        if (!Array.isArray(data?.lines)) throw new Error("Invalid payment schedule response.");
        if (cancelled) return;
        setPacketLineItems(data.lines);
        setPaymentOwnerId(intervention.id);
      } catch (_) {
        if (!cancelled) setPaymentError("Unable to load the payment schedule. Close and reopen this view to retry.");
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    };

    loadPacketLines();
    return () => {
      cancelled = true;
    };
  }, [visible, intervention?.id]);

  const approvedFundingLineItems = useMemo(
    () => resolveApprovedCostLines(intervention),
    [intervention]
  );

  const paymentSummary = useMemo(
    () => buildInterventionPaymentSummary(approvedFundingLineItems, paymentOwnerId === intervention?.id ? packetLineItems : []),
    [approvedFundingLineItems, packetLineItems, paymentOwnerId, intervention?.id]
  );
  const paymentTotalLoading = Boolean(visible && intervention?.id && !paymentError &&
    (paymentLoading || paymentOwnerId !== intervention.id));
  const paymentsToDate = visible && intervention?.id && paymentOwnerId === intervention.id && !paymentLoading && !paymentError
    ? paymentSummary.packets.reduce((cents, packet) => cents + Math.round(packet.amount * 100), 0) / 100
    : null;
  // A saved actual (including zero) or a deliberate edit/clear always wins.
  // Derive the suggestion so late payment data cannot overwrite user input.
  const useCommittedDefault = isClosing && !isCloseReadOnly && !actualCostEdited && closeForm.actualAmount === "";
  const closeActualAmount = useCommittedDefault && paymentsToDate !== null ? String(paymentsToDate) : closeForm.actualAmount;
  const paymentsToDateHint = paymentsToDate !== null
    ? `Total payments to date: ${formatCurrencyDisplay(String(paymentsToDate))} (unsent and sent).`
    : paymentTotalLoading ? "Total payments to date: loading…" : "Total payments to date: unavailable.";

  const paymentsByView = useMemo(() => {
    const groups = { unsent: [], sent: [] };
    paymentSummary.packets.forEach(packet => {
      groups[["submitted", "confirmed"].includes(packet.status) ? "sent" : "unsent"].push(packet);
    });
    return groups;
  }, [paymentSummary.packets]);
  const money = value => value === null || paymentLoading || paymentError || paymentOwnerId !== intervention?.id ? "—" : formatCurrencyDisplay(String(value));
  const paymentsHref = packetId => `/cases/${encodeURIComponent(caseId || intervention?.caseId || "")}?paymentEmailPacket=${encodeURIComponent(packetId)}`;

  const applyFieldSideEffects = (draft, field, value) => {
    const next = { ...draft, [field]: value };

    if (field === "code") {
      if (!requiresNocForCode(value)) {
        next.noc = "";
        next.nocVersion = "";
      } else if (!next.nocVersion) {
        const defaultVersion = pickDefaultNocVersion(nocVersionOptions);
        if (defaultVersion) {
          next.nocVersion = defaultVersion;
        }
      }
    }

    if (field === "nocVersion") {
      next.noc = nocCodesByVersion.current.get(value) ?? "";
    }

    if (field === "startDate" || field === "endDate") {
      const start = field === "startDate" ? value : next.startDate;
      const end = field === "endDate" ? value : next.endDate;
      const duration = calculateDurationDays(start, end);
      next.durationDays = duration !== null ? clampInterventionDurationDaysForIlmp(duration) : "";
    }

    next.status = normalizeEditableInterventionStatus(next.status, "approved");
    next.outcome = normaliseOutcomeForStatus(next.status, next.outcome);

    return next;
  };

  const handleChange = (field, value) => {
    if (isFormReadOnly || (isDetailsOnly && !DIRECT_EDIT_FIELDS.includes(field))) return;
    if (["code", "nocVersion", "noc"].includes(field)) nocSearchSequence.current += 1;
    if (field === "code" || field === "nocVersion") setNocLookupText(null);
    setForm(current => {
      if (field === "nocVersion" && current.nocVersion) {
        // Keep each version's latest draft code separate, including deliberate clears.
        nocCodesByVersion.current.set(current.nocVersion, current.noc);
      }
      if (field === "code") nocCodesByVersion.current.clear();
      const next = applyFieldSideEffects(current, field, value);
      if (field === "code" || field === "nocVersion") {
        setNocSuggestions([]);
        setNocSuggestionsLoading(false);
      }
      return next;
    });
  };

  const handleCloseChange = (field, value) => {
    if (isCloseReadOnly) return;
    if (field === "actualAmount") setActualCostEdited(true);
    setCloseForm(current => {
      const nextValue =
        field === "status"
          ? normalizeInterventionStatus(value, "completed")
          : typeof value === "string"
            ? value.trim()
            : value;
      return { ...current, [field]: nextValue };
    });
  };

  const fetchNocSuggestions = async filteringText => {
    if (isFormReadOnly) {
      setNocSuggestions([]);
      return;
    }
    if (!requiresNoc) {
      setNocSuggestions([]);
      return;
    }
    const versionCode = form.nocVersion || "";
    if (!versionCode) {
      setNocSuggestions([]);
      return;
    }
    const query = (filteringText || "").trim();
    if (!query) {
      setNocSuggestions([]);
      return;
    }
    const sequence = ++nocSearchSequence.current;
    setNocSuggestionsLoading(true);
    try {
      const results = await onSearchNocCodes({ query, version: versionCode });
      if (sequence !== nocSearchSequence.current) return;
      const options = results.map(item => ({
        value: item.code,
        label: `${item.code} – ${item.title}`,
        description: item.title,
      }));
      setNocSuggestions(options);
    } catch (searchError) {
      if (sequence === nocSearchSequence.current) setError(searchError?.message || "Unable to search NOC codes.");
    } finally {
      if (sequence === nocSearchSequence.current) setNocSuggestionsLoading(false);
    }
  };

  const handleCloseSubmit = async () => {
    if (!canClose || typeof onClose !== "function") {
      setError("Closing this intervention is not available.");
      return;
    }
    if (isCloseReadOnly) {
      return;
    }
    if (isDirty) {
      setError("Save your changes before closing this intervention.");
      return;
    }
    const outcomeValue = (closeForm.outcome || "").trim();
    if (!outcomeValue) {
      setError("Select an ESDC outcome before closing this intervention.");
      return;
    }
    const statusValue =
      resolveModalInterventionState({ status: closeForm.status }, "completed").deliveryStatus === "cancelled"
        ? "cancelled"
        : "completed";
    if (useCommittedDefault && paymentTotalLoading) return;
    const actualAmountRaw = (closeActualAmount || "").trim();
    if (actualAmountRaw) {
      if (!isValidOptionalCurrencyAmount(actualAmountRaw)) {
        setError(`Actual cost ${CURRENCY_AMOUNT_RANGE_MESSAGE}.`);
        return;
      }
    }
    const actualAmountValue = parseOptionalCurrencyAmount(actualAmountRaw);
    if (!closeForm.completionDate) {
      setError("Completion date is required to close this intervention.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onClose({
        status: statusValue,
        deliveryStatus: statusValue,
        outcome: outcomeValue,
        actualAmount: actualAmountValue,
        completionDate: closeForm.completionDate || null,
        ...(!isDetailsOnly ? { notes: form.notes?.trim() ? form.notes.trim() : null } : {}),
      });
    } catch (closeError) {
      setError(closeError?.message || "Unable to close intervention.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const handleDetailsSubmit = async () => {
    if (!canEditApprovedDetails) return;
    const changes = Object.fromEntries(DIRECT_EDIT_FIELDS
      .filter(key => normalizeDetailValue(form[key]) !== normalizeDetailValue(initialFormRef.current[key]))
      .map(key => [key, normalizeDetailValue(form[key])]));
    if (!Object.keys(changes).length) return;
    const errors = {};
    if (requiresNoc && (Object.hasOwn(changes, "noc") || Object.hasOwn(changes, "nocVersion"))) {
      if (!form.nocVersion) errors.nocVersion = "Select a NOC version for this intervention.";
      const expectedLength = form.nocVersion === "2021" ? 5 : 4;
      if (!/^\d+$/.test(form.noc.trim()) || form.noc.trim().length !== expectedLength) {
        errors.noc = `Select a valid ${expectedLength}-digit NOC code for version ${form.nocVersion}.`;
      }
    }
    setFieldErrors(errors);
    setValidationError(Object.keys(errors).length ? "Please resolve the highlighted fields." : null);
    if (Object.keys(errors).length) return;
    setLoading(true);
    setError(null);
    try {
      await onSubmitDetails({ detailsVersion: intervention.detailsVersion, ...changes });
      nocCodesByVersion.current.clear();
    } catch (submitError) {
      setError(submitError?.message || "Unable to save intervention details.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (isClosing) {
      await handleCloseSubmit();
      return;
    }
    if (mode === "edit" && !isEditing) return;
    if (isAccessReadOnly) return;
    if (isDetailsOnly) {
      await handleDetailsSubmit();
      return;
    }
    setValidationError(null);
    setFieldErrors({});
    const statusState = resolveModalInterventionState({ status: form.status }, "approved");
    const statusNormalized = statusState.effectiveStatus || normalizeInterventionStatus(form.status, "approved");
    const futureError = getFutureInterventionDeliveryError({ startDate: form.startDate, deliveryStatus: statusState.deliveryStatus });
    if (futureError) { setFutureDateError(futureError); return; }
    const outcomeValue = normaliseOutcomeForStatus(statusNormalized, form.outcome);
    const trimmedCode = (form.code ?? "").toString().trim();
    const errors = {};
    if (!trimmedCode) {
      errors.code = "Intervention code is required.";
    }
    // Funding stream is inherited from the action plan; no intervention-level validation.
    if (!form.startDate) {
      errors.startDate = "Start date is required.";
    }
    if (requiresNoc) {
      if (!form.nocVersion) {
        errors.nocVersion = "Select a NOC version for this intervention.";
      }
      if (!form.noc.trim()) {
        errors.noc = "Select a NOC code for this intervention.";
      }
      const expectedLength = form.nocVersion === "2021" ? 5 : 4;
      const nocValue = form.noc.trim();
      if (nocValue.length !== expectedLength || !/^\d+$/.test(nocValue)) {
        errors.noc = `NOC code must be a ${expectedLength}-digit numeric value for version ${form.nocVersion}.`;
      }
    }
    if (form.startDate) {
      const startDateObj = new Date(form.startDate);
      const cutoff = new Date("2000-01-01");
      if (startDateObj < cutoff) {
        errors.startDate = "Start date must be after 2000-01-01.";
      }
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      errors.endDate = "End date cannot be before start date.";
    }
    if (planStartDate) {
      const toDateOnly = value => {
        if (!value) return null;
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        return parsed.toISOString().slice(0, 10);
      };
      const planStart = toDateOnly(planStartDate);
      const interventionStart = toDateOnly(form.startDate);
      if (planStart && interventionStart && interventionStart < planStart) {
        errors.startDate = "Intervention start date cannot be before the action plan start date.";
      }
    }
    if (form.startDate && form.endDate) {
      const maxEnd = new Date(form.startDate);
      maxEnd.setMonth(maxEnd.getMonth() + 60);
      const end = new Date(form.endDate);
      if (end > maxEnd) {
        errors.endDate = "End date must be within 60 months of start date.";
      }
    }
    const durationValue =
      form.durationDays === "" ? null : Number(form.durationDays.replace(/\s+/g, ""));
    if (form.durationDays !== "" && !Number.isFinite(durationValue)) {
      errors.durationDays = "Duration (days) must be a number.";
    }
    if (Number.isFinite(durationValue) && durationValue < 0) {
      errors.durationDays = "Duration (days) cannot be negative.";
    }
    if (form.startDate && form.endDate && Number.isFinite(durationValue)) {
      const rangeDays = calculateDurationDays(form.startDate, form.endDate);
      if (rangeDays !== null && durationValue > rangeDays) {
        errors.durationDays = "Duration (days) must not exceed the span between start and end date.";
      }
      if (durationValue > 999) {
        errors.durationDays = "Duration (days) must be 0–999.";
      }
    }

    const costValue = parseOptionalCurrencyAmount(form.cost);
    if (form.cost !== "" && !Number.isFinite(costValue)) {
      errors.cost = "Cost must be a valid dollar amount.";
    }
    if (!errors.cost && !isValidOptionalCurrencyAmount(form.cost)) {
      errors.cost = `Cost ${CURRENCY_AMOUNT_RANGE_MESSAGE}.`;
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setValidationError("Please resolve the highlighted fields.");
      return;
    }

    const payload = {
      code: trimmedCode,
      status: statusNormalized,
      deliveryStatus: statusState.deliveryStatus || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      durationDays: durationValue,
      outcome: outcomeValue || null,
      cost: costValue,
      potId: inheritedBudgetPot || null,
      postingContext: form.postingContext || "external",
      fundingStream: inheritedFundingStream || null,
      notes: form.notes.trim() || null,
      noc: form.noc.trim() || null,
      nocVersion: form.nocVersion.trim() || null,
      deliveryMode: form.deliveryMode === "in_house" ? "in_house" : "partner",
      institution: form.institution.trim() || null,
      programName: form.programName.trim() || null,
      itpDetails: form.itpDetails.trim() || null,
      wageSubsidyDetails: form.wageSubsidyDetails.trim() || null,
      metadata: {
        deliveryMode: form.deliveryMode === "in_house" ? "in_house" : "partner",
        institution: form.institution.trim() || null,
        trainingInstitution: form.institution.trim() || null,
        programName: form.programName.trim() || null,
        itpDetails: form.itpDetails.trim() || null,
        wageSubsidyDetails: form.wageSubsidyDetails.trim() || null,
      },
    };

    setLoading(true);
    try {
      await onSubmit(payload);
      nocCodesByVersion.current.clear();
    } catch (submitError) {
      if (submitError?.code === FUTURE_INTERVENTION_START_ERROR) {
        setFutureDateError(submitError.details || submitError);
        setLoading(false);
        return;
      }
      if (["missing_internal_gl_code", "missing_external_gl_code", "posting_context_not_permitted"].includes(submitError?.code)) {
        setFieldErrors(prev => ({ ...prev, postingContext: submitError?.message || "Check Paid from selection." }));
        setValidationError(submitError?.message || "Check Paid from selection.");
        setLoading(false);
        return;
      }
      setError(submitError?.message || "Unable to save intervention.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const isDirty = useMemo(() => {
    const initial = initialFormRef.current;
    if (isDetailsOnly) return DIRECT_EDIT_FIELDS.some(key => normalizeDetailValue(form[key]) !== normalizeDetailValue(initial[key]));
    return FORM_KEYS.some(key => (form[key] ?? "") !== (initial[key] ?? ""));
  }, [form, isDetailsOnly]);

  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!visible || isDetailsOnly) return;
    if (dirtyRef.current) return;
    if (!requiresNoc) return;
    if (form.nocVersion) return;
    const defaultVersion = pickDefaultNocVersion(nocVersionOptions);
    if (!defaultVersion) return;

    setForm(current => {
      if (current.nocVersion) return current;
      const next = { ...current, nocVersion: defaultVersion };
      initialFormRef.current = { ...initialFormRef.current, nocVersion: defaultVersion };
      return next;
    });
  }, [visible, requiresNoc, form.nocVersion, nocVersionOptions, isDetailsOnly]);

  const handleCancel = () => {
    if (loading) return;
    nocSearchSequence.current += 1;
    nocCodesByVersion.current.clear();
    setNocLookupText(null);
    if (mode === "edit" && isEditing) {
      setForm({ ...initialFormRef.current });
      setError(null);
      setValidationError(null);
      setFieldErrors({});
      setNocSuggestions([]);
      setNocSuggestionsLoading(false);
      setIsEditing(false);
      return;
    }
    setForm({ ...initialFormRef.current });
    setError(null);
    setValidationError(null);
    setFieldErrors({});
    setNocSuggestions([]);
    setNocSuggestionsLoading(false);
    setIsClosing(false);
    setIsEditing(mode !== "edit");
    setCloseForm(buildCloseForm(intervention));
    setActualCostEdited(false);
    if (typeof onDismiss === "function") {
      onDismiss();
    }
  };

  const saveDisabled =
    isFormReadOnly ||
    (!isDetailsOnly && (codesLoading || outcomesLoading || fundingStreamsLoading)) ||
    (requiresNoc && nocVersionsLoading) ||
    loading ||
    !isDirty;

  const beginClosing = () => {
    if (!canClose) return;
    if (isDirty) {
      setError("Save your changes before closing this intervention.");
      return;
    }
    setCloseForm(buildCloseForm(intervention));
    setActualCostEdited(false);
    setIsClosing(true);
    setIsEditing(false);
    setError(null);
  };

  const closeDisabled =
    !isClosing ||
    loading ||
    (useCommittedDefault && paymentTotalLoading) ||
    !canClose ||
    isCloseReadOnly ||
    !closeForm.outcome ||
    !closeForm.outcome.trim() ||
    isDirty;

  const activityTitle = form.programName.trim() || selectedCodeOption?.label?.replace(/^\d+\s*[–-]\s*/, "") || "Intervention details";
  const activitySubtitle = form.institution || (!requiresExternalPartner ? selectedDeliveryModeOption?.label : "Provider not recorded");

  const nocEditor = requiresNoc && (
    <section className="intervention-detail-section">
      <div className="intervention-section-heading"><h3>Occupational classification</h3></div>
      <div className="intervention-noc-fields">
        <FormField stretch label="NOC lookup" errorText={fieldErrors.noc}>
          <Autosuggest
            value={nocLookupText ?? currentNocDisplay?.title ?? form.noc}
            onChange={({ detail }) => {
              const value = detail.value || "";
              setNocLookupText(value);
              // Lookup text is presentation only; save the numeric ILMP code.
              handleChange("noc", /^\d*$/.test(value.trim()) ? value.trim() : "");
              if (value.length >= 2) {
                fetchNocSuggestions(value);
              } else {
                setNocSuggestions([]);
                setNocSuggestionsLoading(false);
              }
            }}
            onSelect={({ detail }) => {
              const option = detail.selectedOption;
              if (!option) return;
              handleChange("noc", option.value || "");
              setNocSuggestionsLoading(false);
              setNocLookupText(option.description || option.value || "");
            }}
            options={nocSuggestions}
            filteringType="manual"
            statusType={nocSuggestionsLoading ? "loading" : "finished"}
            expandToViewport
            placeholder={form.nocVersion ? "Search by occupation or code" : "Select a NOC version first"}
            empty="No NOC matches found."
            readOnly={isFormReadOnly}
            disabled={!isFormReadOnly && (nocVersionsLoading || !form.nocVersion)}
            enteredTextLabel={value => `Search for "${value}"`}
            onLoadItems={({ detail }) => fetchNocSuggestions(detail.filteringText)}
            invalid={Boolean(fieldErrors.noc)}
            spellcheck={false}
          />
        </FormField>
        <FormField stretch label="NOC code">
          <Input
            value={form.noc}
            onChange={({ detail }) => {
              setNocLookupText(null);
              setNocSuggestions([]);
              setNocSuggestionsLoading(false);
              handleChange("noc", detail.value || "");
            }}
            readOnly={isFormReadOnly}
            disabled={!isFormReadOnly && (nocVersionsLoading || !form.nocVersion)}
            invalid={Boolean(fieldErrors.noc)}
            spellcheck={false}
          />
        </FormField>
        <FormField stretch label="NOC version" errorText={fieldErrors.nocVersion}>
          <Select
            selectedOption={selectedNocVersionOption && { ...selectedNocVersionOption, label: selectedNocVersionOption.value }}
            onChange={({ detail }) => handleChange("nocVersion", detail.selectedOption?.value || "")}
            options={nocVersionOptions}
            expandToViewport
            placeholder="Version"
            statusType={nocVersionsLoading ? "loading" : "finished"}
            empty={nocVersionsLoading ? undefined : "No NOC versions available. Please try again later."}
            readOnly={isFormReadOnly}
            disabled={!isFormReadOnly && nocVersionsLoading}
            invalid={Boolean(fieldErrors.nocVersion)}
          />
        </FormField>
      </div>
    </section>
  );

  return (
    <>
    <FutureInterventionDateModal error={visible ? futureDateError : null} onDismiss={() => setFutureDateError(null)} />
    <Modal
      visible={visible && !futureDateError}
      header={modalHeaderContent}
      onDismiss={handleCancel}
      closeAriaLabel={
        mode === "edit"
          ? isFormReadOnly
            ? "Close view intervention modal"
            : "Close edit intervention modal"
          : "Close new intervention modal"
      }
      size="large"
      disableContentPaddings
      footer={
        <div className="intervention-modal-footer">
          <Button className="intervention-modal-dismiss" onClick={handleCancel} disabled={loading}>
            {isFormReadOnly && !isClosing ? "Done" : "Cancel"}
          </Button>
          {mode === "edit" && !isAccessReadOnly && !isClosing && !isEditing && !startInCloseMode && (
            <Button variant="primary" onClick={() => setIsEditing(true)} disabled={loading}>
              Edit
            </Button>
          )}
          {mode === "edit" && canClose && !isClosing && !isEditing && (
            <Button onClick={beginClosing} disabled={loading}>
              Close intervention
            </Button>
          )}
          {mode === "edit" && !isClosing && !isEditing && typeof onRevise === "function" && (
            <Button
              variant={isAccessReadOnly ? "primary" : "normal"}
              onClick={() => onRevise(intervention)}
              disabled={loading}
            >
              {revisionActionLabel}
            </Button>
          )}
          {((mode !== "edit") || (mode === "edit" && isEditing)) && !isClosing && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={saveDisabled}
            >
              {mode === "edit" ? "Save changes" : "Create intervention"}
            </Button>
          )}
          {isClosing && !isCloseReadOnly && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={closeDisabled}
            >
              Close intervention
            </Button>
          )}
        </div>
      }
    >
      <div className="intervention-detail">
        <div className="intervention-detail-hero">
          <div className="intervention-detail-heading">
            <div className="intervention-detail-eyebrow">{selectedCodeOption?.label || "Intervention"}</div>
            <h2>{activityTitle}</h2>
            <p className="intervention-detail-provider">{activitySubtitle}</p>
            <div className="intervention-detail-meta">
              <span className={`intervention-status intervention-status--${form.status}`}>{selectedStatusOption?.label || form.status}</span>
              <span className="intervention-detail-dates">
                <span><span className="intervention-sr-only">Start date: </span><strong>{formatDetailDate(form.startDate)}</strong></span>
                <span aria-hidden="true"> – </span>
                <span><span className="intervention-sr-only">End date: </span><strong>{formatDetailDate(form.endDate)}</strong></span>
                {form.durationDays !== "" && <span className="intervention-detail-duration">({form.durationDays} {Number(form.durationDays) === 1 ? "day" : "days"})</span>}
              </span>
            </div>
          </div>
          <div className="intervention-detail-cost">
            <span className="intervention-detail-cost-label">Planned cost</span>
            <strong>{getCurrencyInputDisplayValue(costInputValue, false) || "Not recorded"}</strong>
            <dl className="intervention-hero-funding">
              <SummaryField label="Funding stream">
                {mode === "edit" ? recordedFundingStream || "Not recorded" : inheritedFundingStream || "Not set"}
                {fundingStreamReference && <span className="intervention-funding-reference">{fundingStreamReference}</span>}
              </SummaryField>
            </dl>
          </div>
        </div>
        <div className="intervention-detail-body">
          <SpaceBetween size="m">
        {(error || validationError) && (
          <Alert
            type="error"
            dismissible
            dismissAriaLabel="Dismiss error message"
            onDismiss={() => {
              setError(null);
              setValidationError(null);
            }}
          >
            {error || validationError}
          </Alert>
        )}
        {!isFormReadOnly && !isDetailsOnly && <Box color="text-body-secondary" fontSize="body-s">
          Keep future interventions Approved until they start. Future-dated interventions cannot be activated or suspended. Use "Close intervention" to record the final outcome, completion date, and actual spend. Activating an intervention will also activate its parent action plan if it is still in draft.
        </Box>}
        {isInterventionCancelledBeforeStart(intervention) && <Alert type="info" header="Cancelled before starting">
          {intervention?.metadata?.cancellation?.reason}
        </Alert>}
        {mode === "edit" && !isInterventionCancelledBeforeStart(intervention) && (isClosing || (isClosedIntervention && !isEditing)) && (
          <SpaceBetween size="s">
            <Header variant="h3">Close intervention</Header>
            {showCloseGuidance && isClosing && canClose && (
              <Alert
                type={isDirty ? "warning" : "info"}
                dismissible
                dismissAriaLabel="Dismiss close guidance"
                onDismiss={() => setShowCloseGuidance(false)}
              >
                {isDirty
                  ? "Save pending edits before closing. Then pick an outcome, closure status, and final amounts to record the intervention."
                  : "Choose the outcome, closure status, completion date, and actual cost (if applicable) to close this intervention."}
              </Alert>
            )}
            <ColumnLayout columns={3} variant="text-grid">
              <FormField label="ESDC outcome" description="Required to close.">
                <Select
                  selectedOption={selectedCloseOutcomeOption}
                  onChange={({ detail }) =>
                    handleCloseChange("outcome", detail.selectedOption?.value || "")
                  }
                  options={outcomeSelectOptions}
                  filteringType="auto"
                  placeholder="Select outcome"
                  readOnly={isCloseReadOnly}
                />
              </FormField>
              <FormField label="Closure status" description="Required to close.">
                <Select
                  selectedOption={selectedCloseStatusOption}
                  onChange={({ detail }) =>
                    handleCloseChange("status", detail.selectedOption?.value || "completed")
                  }
                  options={CLOSE_STATUS_OPTIONS}
                  readOnly={isCloseReadOnly}
                />
              </FormField>
              <FormField label="Completion date" description="Required to close">
                <DatePicker className="intervention-date-control"
                  value={closeForm.completionDate}
                  onChange={({ detail }) => handleCloseChange("completionDate", detail.value)}
                  placeholder="YYYY-MM-DD"
                  readOnly={isCloseReadOnly}
                />
              </FormField>
              <FormField
                label="Actual cost"
                description={paymentsToDateHint}
              >
                <Input
                  value={getCurrencyInputDisplayValue(closeActualAmount, isActualCostFocused)}
                  onChange={({ detail }) => handleCloseChange("actualAmount", detail.value)}
                  onFocus={() => setIsActualCostFocused(true)}
                  onBlur={() => setIsActualCostFocused(false)}
                  placeholder="e.g. 4200"
                  spellcheck={false}
                  readOnly={isCloseReadOnly}
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        )}


            {!isFormReadOnly && isDetailsOnly && (
              <div className="intervention-edit-fields">
                <Box color="text-body-secondary" fontSize="body-s">
                  Update notes, descriptive details and occupational classification without another approval. Use Revise for changes to approved terms.
                </Box>
                <section className="intervention-detail-section">
                  <SpaceBetween size="m">
                    {isEducationIntervention && <FormField stretch label="Training plan details">
                      <Textarea value={form.itpDetails} rows={3} spellcheck
                        onChange={({ detail }) => handleChange("itpDetails", detail.value)} />
                    </FormField>}
                    {isWageSubsidyIntervention && <FormField stretch label="Wage subsidy details">
                      <Textarea value={form.wageSubsidyDetails} rows={3} spellcheck
                        onChange={({ detail }) => handleChange("wageSubsidyDetails", detail.value)} />
                    </FormField>}
                    <FormField stretch label="Notes">
                      <Textarea value={form.notes} rows={3} spellcheck
                        onChange={({ detail }) => handleChange("notes", detail.value)} />
                    </FormField>
                  </SpaceBetween>
                </section>
                {nocEditor}
              </div>
            )}
            {!isFormReadOnly && !isDetailsOnly && (
              <div className="intervention-edit-fields">
                <section className="intervention-detail-section">
                  <div className="intervention-section-heading"><h3>Activity and provider</h3></div>
                  <SpaceBetween size="l">
            <ColumnLayout columns={2} minColumnWidth={280}>
              {isClosedStatusValue(form.status) && !isClosing && (
                <FormField stretch label="ESDC outcome">
                  <Input value={outcomeLabel} readOnly />
                </FormField>
          )}
              <FormField label="Intervention code" stretch errorText={fieldErrors.code}>
                <Select
                  selectedOption={selectedCodeOption}
                  onChange={({ detail }) => handleChange("code", detail.selectedOption?.value || "")}
                  options={selectOptions}
                  filteringType="auto"
                  placeholder={codesLoading ? "Loading intervention codes" : "Select intervention code"}
                  statusType={codesLoading ? "loading" : "finished"}
                  empty={
                    codesLoading
                      ? undefined
                      : "No intervention codes available. Please try again later."
                  }
                  readOnly={isFormReadOnly}
                  disabled={!isFormReadOnly && codesLoading}
                  autoFocus={!isFormReadOnly}
                  invalid={Boolean(fieldErrors.code)}
                />
              </FormField>
              {!requiresExternalPartner && (
                <>
                  <FormField stretch label="Delivery mode" description="Choose how this will run.">
                    <Select
                      selectedOption={selectedDeliveryModeOption}
                      onChange={({ detail }) => handleChange("deliveryMode", detail.selectedOption?.value || "partner")}
                      options={[
                        { value: "partner", label: "External delivery partner" },
                        { value: "in_house", label: "In-house (no external partner)" },
                      ]}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                  {form.deliveryMode !== "in_house" ? (
                    <FormField stretch label="Delivery partner / provider">
                      <Input
                        value={form.institution}
                        onChange={({ detail }) => handleChange("institution", detail.value)}
                        spellcheck={false}
                        readOnly={isFormReadOnly}
                      />
                    </FormField>
                  ) : (
                    <Box />
                  )}
                </>
              )}
            </ColumnLayout>
            {isEducationIntervention && (
              <Grid gridDefinition={[
                { colspan: { default: 12, xs: 6 } },
                { colspan: { default: 12, xs: 6 } },
                { colspan: 12 },
              ]}>
                  <FormField stretch label="Institution" description="Training provider or school delivering the program.">
                    <Input
                      value={form.institution}
                      onChange={({ detail }) => handleChange("institution", detail.value)}
                      spellcheck={false}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                  <FormField stretch label="Program name (optional)" description="Course, credential, or stream name.">
                    <Input
                      value={form.programName}
                      onChange={({ detail }) => handleChange("programName", detail.value)}
                      spellcheck={false}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                <FormField
                  stretch
                  label="Training plan details"
                  description="Describe what the client will learn, key milestones, any supports or materials needed, and how the training supports their employment goal."
                >
                  <Textarea
                    value={form.itpDetails}
                    rows={3}
                    onChange={({ detail }) => handleChange("itpDetails", detail.value)}
                    spellcheck={true}
                    readOnly={isFormReadOnly}
                  />
                </FormField>
              </Grid>
            )}
            {isEmployerIntervention && (
              <SpaceBetween size="s">
                <ColumnLayout columns={2}>
                  <FormField stretch
                    label="Employer / delivery partner"
                    description="Employer or host organization providing the placement."
                  >
                    <Input
                      value={form.institution}
                      onChange={({ detail }) => handleChange("institution", detail.value)}
                      spellcheck={false}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                  <FormField stretch label="Program name (optional)" description="Job title, role, or program name.">
                    <Input
                      value={form.programName}
                      onChange={({ detail }) => handleChange("programName", detail.value)}
                      spellcheck={false}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                </ColumnLayout>
                {isWageSubsidyIntervention && (
                  <FormField stretch label="Wage subsidy details">
                    <Textarea
                      value={form.wageSubsidyDetails}
                      rows={3}
                      onChange={({ detail }) => handleChange("wageSubsidyDetails", detail.value)}
                      spellcheck={true}
                      readOnly={isFormReadOnly}
                    />
                  </FormField>
                )}
              </SpaceBetween>
            )}

                  </SpaceBetween>
                </section>
                <section className="intervention-detail-section">
                  <div className="intervention-section-heading"><h3>Status and dates</h3></div>
            <ColumnLayout columns={2} minColumnWidth={280}>
              <FormField stretch label="Status">
                <Select
                  selectedOption={selectedStatusOption}
                  onChange={({ detail }) => handleChange("status", detail.selectedOption?.value || "approved")}
                  options={statusOptions}
                  readOnly={isFormReadOnly}
                />
              </FormField>
              <FormField stretch label="Start date" errorText={fieldErrors.startDate}>
                <DatePicker className="intervention-date-control"
                  value={form.startDate}
                  onChange={({ detail }) => handleChange("startDate", detail.value)}
                  placeholder="YYYY-MM-DD"
                  readOnly={isFormReadOnly}
                />
              </FormField>
              <FormField stretch label="End date" errorText={fieldErrors.endDate}>
                <DatePicker className="intervention-date-control"
                  value={form.endDate}
                  onChange={({ detail }) => handleChange("endDate", detail.value)}
                  placeholder="YYYY-MM-DD"
                  readOnly={isFormReadOnly}
                />
              </FormField>
              <FormField stretch label="Duration in days (calculated)" errorText={fieldErrors.durationDays}>
                <Input
                  value={form.durationDays}
                  readOnly
                  type="number"
                />
              </FormField>
            </ColumnLayout>
                </section>
                {nocEditor}
                <section className="intervention-detail-section">
                  <div className="intervention-section-heading"><h3>Financial details</h3></div>
            <Grid gridDefinition={[
              { colspan: { default: 12, xs: 6 } },
              { colspan: { default: 12, xs: 6 } },
              { colspan: { default: 12, xs: 6 } },
              { colspan: { default: 12, xs: 6 } },
            ]}>
              <FormField stretch label="Funding Stream" constraintText="Inherited. Adjust in parent Action Plan."
                description={mode === "edit" ? `Recorded intervention decision: ${recordedFundingStream || "Not recorded"}.` : undefined}>
                <Input value={inheritedFundingStream || "Not set"} readOnly />
              </FormField>
              <FormField stretch label="Budget Pot" constraintText="Inherited. Adjust in parent Action Plan.">
                <Input value={inheritedBudgetPotLabel} readOnly />
              </FormField>
              <FormField
                stretch
                label="Paid from"
                constraintText="Select whether this pot is charged externally or internally."
                errorText={fieldErrors.postingContext}
              >
                {isAssessor || !canSelectPostingContext ? (
                  <Input value="External (region/PTMA)" readOnly />
                ) : (
                  <Select
                    selectedOption={selectedPostingContext}
                    options={POSTING_CONTEXT_OPTIONS}
                    onChange={({ detail }) => {
                      setFieldErrors(prev => {
                        if (!prev.postingContext) return prev;
                        const next = { ...prev };
                        delete next.postingContext;
                        return next;
                      });
                      setForm(current => ({ ...current, postingContext: detail.selectedOption?.value || "external" }));
                    }}
                    placeholder="Select"
                    readOnly={isFormReadOnly}
                  />
                )}
              </FormField>
              <FormField stretch label="Planned cost" errorText={fieldErrors.cost}>
                <Input
                  value={getCurrencyInputDisplayValue(costInputValue, isCostFocused)}
                  onChange={({ detail }) => handleChange("cost", detail.value)}
                  onFocus={() => setIsCostFocused(true)}
                  onBlur={() => setIsCostFocused(false)}
                  placeholder="e.g. 42000.00"
                  spellcheck={false}
                  readOnly={isFormReadOnly}
                />
              </FormField>
            </Grid>
                </section>
                <section className="intervention-detail-section">
            <FormField label="Notes">
              <Textarea
                value={form.notes}
                rows={3}
                onChange={({ detail }) => handleChange("notes", detail.value)}
                placeholder="Optional context or reminders"
                spellcheck={true}
                readOnly={isFormReadOnly}
              />
            </FormField>
                </section>
              </div>
            )}

            {isFormReadOnly && <details className="intervention-additional-details" key={intervention?.id || "new"}>
              <summary>
                <span>Additional details</span>
                <span className="intervention-details-hint">Notes, classification and budget</span>
              </summary>
              <div className="intervention-additional-content">
            {isFormReadOnly && (isEducationIntervention || isWageSubsidyIntervention || Boolean(form.notes)) && (
              <div className="intervention-context">
                {isEducationIntervention && <section>
                  <h3>Training plan</h3>
                  <p>{form.itpDetails || "No training plan details recorded."}</p>
                </section>}
                {isWageSubsidyIntervention && <section>
                  <h3>Wage subsidy details</h3>
                  <p>{form.wageSubsidyDetails || "No wage subsidy details recorded."}</p>
                </section>}
                {form.notes && <section>
                  <h3>Notes</h3>
                  <p>{form.notes}</p>
                </section>}
              </div>
            )}
                <section>
                  <h3>Classification and delivery</h3>
                  <dl className="intervention-summary-fields">
                    <SummaryField label="Intervention type" wide>{selectedCodeOption?.label || form.code}</SummaryField>
                    {requiresNoc && <SummaryField label="Intervention-related occupation" wide>
                      <span className="intervention-occupation-title">{nocDisplayTitle}</span>
                      {(form.noc || form.nocVersion) && <span className="intervention-occupation-reference">
                        NOC {form.noc || "not recorded"} · {form.nocVersion || "version not recorded"}
                      </span>}
                    </SummaryField>}
                    {!requiresExternalPartner && <SummaryField label="Delivery mode">{selectedDeliveryModeOption?.label}</SummaryField>}
                    {isClosedStatusValue(form.status) && <SummaryField label="ESDC outcome" wide>{outcomeLabel}</SummaryField>}
                  </dl>
                </section>
                <section>
                  <h3>Budget allocation</h3>
                  <dl className="intervention-summary-fields">
                    <SummaryField label="Budget pot" wide>{inheritedBudgetPotLabel}<FiscalFundingSummary interventions={intervention ? [intervention] : []} /></SummaryField>
                    <SummaryField label="Paid from" wide>{isAssessor || !canSelectPostingContext ? "External (region/PTMA)" : selectedPostingContext?.label}</SummaryField>
                  </dl>
                </section>
              </div>
            </details>}

            {!(isDetailsOnly && isEditing) && <section className="intervention-detail-section intervention-funding-section">
              <div className="intervention-section-heading">
                <h3>Approved funding <span className="intervention-section-count">{approvedFundingLineItems.length}</span></h3>
              </div>
              {paymentSummary.funding.length ? (
                <div className="intervention-funding-table-wrap" role="region" aria-label="Approved funding details" tabIndex={0}>
                  <table className="intervention-funding-table">
                    <caption className="intervention-sr-only">Approved funding lines</caption>
                    <thead><tr>
                      <th scope="col">Support / payee</th>
                      <th scope="col">Approved amount</th>
                      <th scope="col">Remaining</th>
                    </tr></thead>
                    <tbody>{paymentSummary.funding.map(item => (
                      <tr key={item.id}>
                        <th scope="row" className="intervention-funding-support">
                          <span>{paymentTypeLabel(item.paymentType)}</span>
                          <span className="intervention-funding-payee">{item.payeeName}</span>
                        </th>
                        <td className="intervention-funding-amount" data-label="Approved amount">{item.amount}</td>
                        <td className="intervention-funding-remaining" data-label="Remaining">{money(item.remaining)}</td>

                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="intervention-detail-empty">No approved funding lines are stored for this intervention.</p>}
              {paymentError && <Alert type="error">{paymentError}</Alert>}
              {paymentSummary.unmatched && <Alert type="warning">Some payment lines could not be matched to an approved funding line. Funding balances are unavailable until those links are reviewed.</Alert>}
            </section>}

            {!(isDetailsOnly && isEditing) && <section className="intervention-detail-section intervention-packets-section">
              <details className="intervention-payment-details" key={intervention?.id || "new"}>
                <summary>{paymentTerms.title} <span className="intervention-packet-count">({paymentSummary.packets.length})</span></summary>
                <div className="intervention-payment-content">
            <Table
              items={paymentsByView[paymentView]}
              trackBy="id"
              variant="embedded"
              wrapLines
              loading={paymentLoading}
              loadingText={paymentTerms.loading}
              columnDefinitions={[
                { id: "packet", header: paymentTerms.identifier, cell: item => <Link href={paymentsHref(item.id)} onFollow={onOpenPayment ? event => { event.preventDefault(); onOpenPayment(item.id); } : undefined}>#{item.id}</Link> },
                { id: "payments", header: "Payment / payee", cell: item => <SpaceBetween size="xs">{item.lines.map(line => <div key={line.lineId}>{paymentTypeLabel(line.paymentType)} · {line.payeeName}<Box color="text-body-secondary">{formatCurrencyDisplay(String(line.amount))}{line.servicePeriodStart && line.servicePeriodEnd ? ` · ${line.servicePeriodStart} to ${line.servicePeriodEnd}` : ""}</Box></div>)}</SpaceBetween> },
                { id: "due", header: "Due date", cell: item => item.dueBy || "—" },
                { id: "amount", header: "Amount", cell: item => formatCurrencyDisplay(String(item.amount)) },
                { id: "status", header: "Status", cell: item => ({draft: "Draft", ready_to_send: "Ready to send", submitted: "Sent to Finance", confirmed: "Confirmed"}[item.status] || item.status || "—") },
              ]}
              header={<div className="intervention-payment-views" role="group" aria-label={`${paymentTerms.title} delivery status`}>
                {[{ value: "unsent", label: "Unsent" }, { value: "sent", label: "Sent" }].map(view => <button
                  key={view.value}
                  type="button"
                  className="intervention-payment-view"
                  aria-pressed={paymentView === view.value}
                  onClick={() => setPaymentView(view.value)}
                >{view.label} <span>({paymentsByView[view.value].length})</span></button>)}
              </div>}
              empty={<Box padding="s">{paymentError ? `The ${paymentTerms.plural} could not be loaded.` : paymentView === "unsent" ? paymentTerms.emptyUnsent : paymentTerms.emptySent}</Box>}
            />
                </div>
              </details>
              <div className="intervention-packets-action">{intervention?.id && (caseId || intervention?.caseId) && !isInterventionClosedStatus(intervention?.status) ? <Button {...(onPreparePayment ? { onClick: onPreparePayment } : { href: `/cases/${encodeURIComponent(caseId || intervention.caseId)}?preparePaymentIntervention=${encodeURIComponent(intervention.id)}` })}>{paymentTerms.prepare}</Button> : undefined}</div>
            </section>}
          </SpaceBetween>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default InterventionModal;
